import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Wrench } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../services/AuthContext';

/**
 * The shop weekly, on the mechanic's own home screen.
 *
 * The owner, Oct 6 2026, about the weekly shop checklist: "I want that to be a
 * part of the mechanic employee tag. So right now it's James." The list lives
 * on the Mechanic side (/mechanic), so a mechanic signed in on his Employee
 * side saw nothing about it. This card is that list in numbers, with one big
 * button that puts the Mechanic hat on and opens the Weekly tab.
 *
 * Who sees it is the server's call (GET /day/shop-weekly reads the login's own
 * hats): no tag, no card — nothing at all for everybody else. It only reads;
 * the week's list starts when he opens it, not when he looks at this screen.
 */
type Card = { wears: boolean; week_label?: string; answered?: number; total?: number; not_done?: number; blank?: number; in?: boolean; started?: boolean };
// These hats already open the shop (App RequireMechanic), so there is nothing to switch.
const OPENS_THE_SHOP = ['admin', 'manager', 'mechanic'];

export default function ShopWeeklyCard() {
  const { user, switchRole } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Read again every time this screen comes up: he answers lines on the
  // Mechanic side, switches back, and the numbers here must be the new ones.
  const { data } = useQuery<Card>(['shop-weekly-card', user?.id], () => api.get('/day/shop-weekly').then(r => r.data), {
    enabled: !!user, retry: false, refetchOnMount: 'always',
  });
  if (!data?.wears) return null;

  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // After a hat switch the Employee page must not stay one Back away: he
      // would land on it still wearing the Mechanic hat, where jobs, clock and
      // pay are all refused. So the switch REPLACES this page, exactly as the
      // hat switcher does (RoleSwitcher) — Back then leaves the app's shop, it
      // does not half-undo the switch. No switch (the office walking in): a
      // plain step forward, and Back brings them here again.
      const switched = !OPENS_THE_SHOP.includes(String(user?.role));
      if (switched) await switchRole('mechanic');
      navigate('/mechanic?tab=weekly', { replace: switched });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not open the shop list');
      setBusy(false);
    }
  };

  const notDone = Number(data.not_done || 0);
  const blank = Number(data.blank || 0);

  // All in: one quiet line. It is done — it should not shout at him all week.
  if (data.in) {
    return (
      <p className="text-sm text-gray-600 px-1">
        This week's shop list is in.{notDone > 0 && <span className="text-red-700 font-semibold"> {notDone} not done.</span>}{' '}
        {/* A small link, but a thumb-sized target: the padding is the tap area, the negative margin keeps the line one line tall. */}
        <button type="button" onClick={open} disabled={busy} className="inline-block font-semibold text-brand-700 underline px-2 py-3 -my-3 disabled:opacity-50">Open</button>
      </p>
    );
  }

  return (
    <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-300" aria-label="Shop weekly">
      <div className="flex items-center gap-1.5">
        <Wrench className="h-4 w-4 text-slate-600" aria-hidden />
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Shop weekly · {data.week_label}</p>
      </div>
      {data.started ? (
        // The same words, in the same order, as the top of the list it opens.
        <p className="text-lg font-bold text-gray-900 leading-snug mt-1">
          {data.answered} of {data.total} answered
          <span className={notDone ? 'text-red-700' : 'text-gray-500'}> · {notDone} not done</span>
          <span className={blank ? 'text-amber-700' : 'text-gray-500'}> · {blank} blank</span>
        </p>
      ) : (
        <p className="text-lg font-bold text-gray-900 leading-snug mt-1">Not started yet this week</p>
      )}
      <button type="button" onClick={open} disabled={busy} className="btn-primary w-full mt-3 !text-base !py-3.5 disabled:opacity-60">
        {busy ? 'Opening…' : 'Open the shop list'}
      </button>
    </section>
  );
}
